import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Dumbbell, Search, Plus, Check, X, Loader2, Play, ChevronDown, ChevronUp,
  Sparkles, Save, Trash2, ListPlus, Target, Zap, Info, BookOpen, Youtube, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

export default function ExerciseLibrary({ childId, childName }) {
  const { user } = useAuth();
  const [view, setView] = useState('library'); // library, plans
  const [loading, setLoading] = useState(false);
  
  // Library state
  const [exercises, setExercises] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [bodyParts, setBodyParts] = useState([]);
  const [selectedBodyPart, setSelectedBodyPart] = useState('');
  const [searching, setSearching] = useState(false);
  
  // Workout plan state
  const [workoutPlans, setWorkoutPlans] = useState([]);
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [planName, setPlanName] = useState('');
  const [planDescription, setPlanDescription] = useState('');
  const [savingPlan, setSavingPlan] = useState(false);
  
  // Exercise detail modal
  const [expandedExercise, setExpandedExercise] = useState(null);

  const isCoachOrAdmin = user?.role === 'coach' || user?.role === 'admin';

  const loadFilters = async () => {
    try {
      const response = await axios.get(`${API}/exercises/body-parts`);
      setBodyParts(response.data.body_parts || []);
    } catch (error) {
      console.error('Failed to load filters:', error);
    }
  };

  const loadWorkoutPlans = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/exercises/workout-plans/child/${childId}`);
      setWorkoutPlans(response.data.plans || []);
    } catch (error) {
      console.error('Failed to load workout plans:', error);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadFilters();
    if (view === 'plans') {
      loadWorkoutPlans();
    }
  }, [childId, view, loadWorkoutPlans]);

  const searchExercises = async () => {
    if (!selectedBodyPart && !searchQuery) {
      toast.error('Please select a body part or enter a search term');
      return;
    }
    
    setSearching(true);
    try {
      const params = { limit: 20 };
      if (searchQuery) params.query = searchQuery;
      if (selectedBodyPart) params.body_part = selectedBodyPart;
      
      const response = await axios.get(`${API}/exercises/search`, { params });
      setExercises(response.data.exercises || []);
      
      if (response.data.exercises?.length === 0) {
        toast.info('No exercises found. Try different filters.');
      }
    } catch (error) {
      toast.error('Failed to search exercises');
      console.error(error);
    } finally {
      setSearching(false);
    }
  };

  const toggleExerciseSelection = (exercise) => {
    const exists = selectedExercises.find(e => e.exercise_id === exercise.id);
    if (exists) {
      setSelectedExercises(selectedExercises.filter(e => e.exercise_id !== exercise.id));
    } else {
      setSelectedExercises([...selectedExercises, {
        exercise_id: exercise.id,
        name: exercise.name,
        body_part: exercise.body_part,
        target: exercise.target,
        equipment: exercise.equipment,
        instructions: exercise.instructions || [],
        sets: 3,
        reps: "10-12"
      }]);
    }
  };

  const updateExerciseSetsReps = (exerciseId, field, value) => {
    setSelectedExercises(selectedExercises.map(e => 
      e.exercise_id === exerciseId ? { ...e, [field]: value } : e
    ));
  };

  const saveWorkoutPlan = async () => {
    if (!planName.trim()) {
      toast.error('Please enter a plan name');
      return;
    }
    if (selectedExercises.length === 0) {
      toast.error('Please select at least one exercise');
      return;
    }

    setSavingPlan(true);
    try {
      const response = await axios.post(`${API}/exercises/workout-plans`, {
        child_id: childId,
        name: planName,
        description: planDescription,
        exercises: selectedExercises,
        frequency: "3x per week"
      });
      
      toast.success('Workout plan saved!');
      setShowCreatePlan(false);
      setPlanName('');
      setPlanDescription('');
      setSelectedExercises([]);
      setView('plans');
      loadWorkoutPlans();
    } catch (error) {
      console.error('Save plan error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save workout plan');
    } finally {
      setSavingPlan(false);
    }
  };

  const deleteWorkoutPlan = async (planId) => {
    if (!window.confirm('Delete this workout plan?')) return;
    
    try {
      await axios.delete(`${API}/exercises/workout-plans/${planId}`);
      toast.success('Workout plan deleted');
      loadWorkoutPlans();
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  // Generate YouTube search URL for exercise tutorial
  const getYouTubeSearchUrl = (exerciseName) => {
    const searchQuery = encodeURIComponent(`${exerciseName} exercise tutorial how to`);
    return `https://www.youtube.com/results?search_query=${searchQuery}`;
  };

  const ExerciseCard = ({ exercise, showSelect = false, isSelected = false }) => {
    const isExpanded = expandedExercise === exercise.id;
    
    return (
      <div 
        className={`bg-zinc-800/50 rounded-lg overflow-hidden border transition-all ${
          isSelected ? 'border-[#02b5ff] ring-1 ring-[#02b5ff]' : 'border-zinc-700 hover:border-zinc-600'
        }`}
      >
        {/* Header with icon */}
        <div className="relative bg-gradient-to-br from-zinc-800 to-zinc-900 p-4">
          <div className="w-12 h-12 bg-gradient-to-br from-[#02b5ff]/20 to-[#ff025f]/20 rounded-full flex items-center justify-center mx-auto">
            <Dumbbell className="h-6 w-6 text-[#02b5ff]" />
          </div>
          {showSelect && (
            <button
              onClick={(e) => { e.stopPropagation(); toggleExerciseSelection(exercise); }}
              className={`absolute top-2 right-2 p-2 rounded-full transition-all ${
                isSelected 
                  ? 'bg-[#02b5ff] text-white' 
                  : 'bg-zinc-900/80 text-zinc-400 hover:bg-zinc-800'
              }`}
            >
              {isSelected ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            </button>
          )}
        </div>
        
        {/* Info */}
        <div className="p-3">
          <h4 className="font-medium text-white text-sm line-clamp-2 mb-2 capitalize">{exercise.name}</h4>
          <div className="flex flex-wrap gap-1 mb-2">
            <span className="text-[10px] bg-[#02b5ff]/20 text-[#02b5ff] px-2 py-0.5 rounded capitalize">
              {exercise.body_part}
            </span>
            <span className="text-[10px] bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded capitalize">
              {exercise.target}
            </span>
          </div>
          <p className="text-[10px] text-zinc-500 capitalize">{exercise.equipment}</p>
          
          {/* View Instructions Button */}
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={() => setExpandedExercise(isExpanded ? null : exercise.id)}
              className="flex items-center gap-1 text-[#02b5ff] text-xs hover:underline"
            >
              <BookOpen className="h-3 w-3" />
              {isExpanded ? 'Hide' : 'View'} Instructions
              {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
            <a
              href={getYouTubeSearchUrl(exercise.name)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-red-500 text-xs hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              <Youtube className="h-3 w-3" />
              Tutorial
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </div>
          
          {/* Instructions Dropdown */}
          {isExpanded && exercise.instructions && exercise.instructions.length > 0 && (
            <div className="mt-3 p-3 bg-zinc-900/50 rounded-lg border border-zinc-700">
              <h5 className="text-xs font-semibold text-white mb-2 flex items-center gap-1">
                <Info className="h-3 w-3 text-[#02b5ff]" />
                How to perform:
              </h5>
              <ol className="space-y-2">
                {exercise.instructions.map((step, idx) => (
                  <li key={idx} className="text-xs text-zinc-400 flex gap-2">
                    <span className="text-[#02b5ff] font-bold min-w-[16px]">{idx + 1}.</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4" data-testid="exercise-library">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Dumbbell className="h-5 w-5 text-[#02b5ff]" />
          <h3 className="text-lg font-bold text-white">Exercise Library</h3>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={view === 'library' ? 'default' : 'outline'}
            onClick={() => setView('library')}
            className={view === 'library' ? 'bg-[#02b5ff]' : 'border-zinc-700'}
          >
            <Search className="h-3 w-3 mr-1" />
            Browse
          </Button>
          <Button
            size="sm"
            variant={view === 'plans' ? 'default' : 'outline'}
            onClick={() => setView('plans')}
            className={view === 'plans' ? 'bg-[#02b5ff]' : 'border-zinc-700'}
          >
            <ListPlus className="h-3 w-3 mr-1" />
            Plans {workoutPlans.length > 0 && `(${workoutPlans.length})`}
          </Button>
        </div>
      </div>

      {/* Library View */}
      {view === 'library' && (
        <>
          {/* Search & Filters */}
          <div className="flex flex-col md:flex-row gap-3 mb-4">
            <div className="flex-1">
              <Input
                placeholder="Search exercises..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchExercises()}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <select
              value={selectedBodyPart}
              onChange={(e) => setSelectedBodyPart(e.target.value)}
              className="h-10 px-3 bg-zinc-800 border border-zinc-700 text-white rounded-md"
            >
              <option value="">All Body Parts</option>
              {bodyParts.map(bp => (
                <option key={bp} value={bp} className="capitalize">{bp}</option>
              ))}
            </select>
            <Button 
              onClick={searchExercises} 
              disabled={searching}
              className="bg-[#02b5ff] hover:bg-[#02b5ff]/80"
            >
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              <span className="ml-2">Search</span>
            </Button>
          </div>

          {/* Selected Exercises Bar */}
          {isCoachOrAdmin && selectedExercises.length > 0 && (
            <div className="mb-4 p-3 bg-[#02b5ff]/10 border border-[#02b5ff]/30 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-[#02b5ff]" />
                  <span className="text-sm text-white">{selectedExercises.length} exercises selected</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedExercises([])}
                    className="border-zinc-600 text-zinc-400"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setShowCreatePlan(true)}
                    className="bg-[#02b5ff] hover:bg-[#02b5ff]/80"
                  >
                    <Save className="h-3 w-3 mr-1" />
                    Create Plan
                  </Button>
                </div>
              </div>
              
              {/* Selected exercises list */}
              <div className="mt-3 flex flex-wrap gap-2">
                {selectedExercises.map(ex => (
                  <span 
                    key={ex.exercise_id}
                    className="text-xs bg-zinc-800 px-2 py-1 rounded flex items-center gap-1"
                  >
                    <span className="text-white capitalize">{ex.name}</span>
                    <button 
                      onClick={() => toggleExerciseSelection({ id: ex.exercise_id })}
                      className="text-zinc-500 hover:text-[#ff025f]"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Exercise Grid */}
          {exercises.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {exercises.map(exercise => (
                <ExerciseCard 
                  key={exercise.id} 
                  exercise={exercise}
                  showSelect={isCoachOrAdmin}
                  isSelected={selectedExercises.some(e => e.exercise_id === exercise.id)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-zinc-500">
              <Dumbbell className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Select a body part and search to browse exercises</p>
              <p className="text-sm mt-1">Each exercise includes step-by-step instructions</p>
            </div>
          )}
        </>
      )}

      {/* Plans View */}
      {view === 'plans' && (
        <>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 text-[#02b5ff] animate-spin" />
            </div>
          ) : workoutPlans.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <ListPlus className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No workout plans yet</p>
              <p className="text-sm mt-1">Browse exercises and create a custom plan</p>
              <Button 
                onClick={() => setView('library')} 
                className="mt-4 bg-[#02b5ff]"
              >
                <Search className="h-4 w-4 mr-2" />
                Browse Exercises
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {workoutPlans.map(plan => (
                <div key={plan.id} className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-white">{plan.name}</h4>
                      {plan.description && (
                        <p className="text-sm text-zinc-400 mt-1">{plan.description}</p>
                      )}
                      <div className="flex gap-3 mt-2 text-xs text-zinc-500">
                        <span>{plan.total_exercises || plan.exercises?.length || 0} exercises</span>
                        <span>•</span>
                        <span>{plan.frequency || '3x per week'}</span>
                        <span>•</span>
                        <span>By {plan.created_by_name}</span>
                      </div>
                    </div>
                    {isCoachOrAdmin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteWorkoutPlan(plan.id)}
                        className="text-[#ff025f] hover:bg-[#ff025f]/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  {/* Exercise list */}
                  <div className="space-y-2">
                    {plan.exercises?.map((ex, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-zinc-900/50 rounded p-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[#02b5ff] text-xs font-bold">{idx + 1}</span>
                          <span className="text-sm text-white capitalize">{ex.name}</span>
                          <span className="text-xs text-zinc-500 capitalize">({ex.body_part})</span>
                        </div>
                        <span className="text-xs text-zinc-400">{ex.sets} × {ex.reps}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Create Plan Modal */}
      {showCreatePlan && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto border border-zinc-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Create Workout Plan</h3>
              <button onClick={() => setShowCreatePlan(false)} className="text-zinc-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Plan Name *</label>
                <Input
                  value={planName}
                  onChange={(e) => setPlanName(e.target.value)}
                  placeholder="e.g., Upper Body Strength"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="plan-name-input"
                />
              </div>
              
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Description</label>
                <Input
                  value={planDescription}
                  onChange={(e) => setPlanDescription(e.target.value)}
                  placeholder="Brief description of the workout"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">
                  Selected Exercises ({selectedExercises.length})
                </label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {selectedExercises.map((ex, idx) => (
                    <div key={ex.exercise_id} className="bg-zinc-800 rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-white capitalize">{ex.name}</span>
                        <button 
                          onClick={() => setSelectedExercises(selectedExercises.filter(e => e.exercise_id !== ex.exercise_id))}
                          className="text-zinc-500 hover:text-[#ff025f]"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="text-[10px] text-zinc-500">Sets</label>
                          <Input
                            type="number"
                            min="1"
                            value={ex.sets}
                            onChange={(e) => updateExerciseSetsReps(ex.exercise_id, 'sets', parseInt(e.target.value) || 1)}
                            className="h-8 bg-zinc-700 border-zinc-600 text-white text-sm"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="text-[10px] text-zinc-500">Reps</label>
                          <Input
                            value={ex.reps}
                            onChange={(e) => updateExerciseSetsReps(ex.exercise_id, 'reps', e.target.value)}
                            className="h-8 bg-zinc-700 border-zinc-600 text-white text-sm"
                            placeholder="10-12"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowCreatePlan(false)}
                  className="flex-1 border-zinc-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveWorkoutPlan}
                  disabled={savingPlan || !planName.trim() || selectedExercises.length === 0}
                  className="flex-1 bg-[#02b5ff] hover:bg-[#02b5ff]/80"
                  data-testid="save-plan-btn"
                >
                  {savingPlan ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Plan
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
